// Active grid card — mirrors legacy renderGridCard logic exactly.
import { useNavigate } from "react-router-dom";
import type { GridConfig } from "@/types/api";
import type { UnpairedTrade } from "@/hooks/use-overview";
import type { GridPnl } from "@/hooks/use-grid-pnl";
import { useLivePrice } from "@/providers/ws-provider";
import { fmt, fmtDur } from "@/lib/format";
import Badge from "@/components/common/badge";
import AnomalyBadge from "@/components/common/anomaly-badge";
import { useGridAnomalies } from "@/hooks/use-anomalies";

const TAKER_FEE_RATE = 0.0007;

interface GridCardProps {
  grid: GridConfig;
  unpaired: UnpairedTrade[];
  pnl: GridPnl | undefined;
  volatility: Record<string, { mode?: string; trend?: string }>;
}

export default function GridCard({ grid, unpaired, pnl, volatility }: GridCardProps) {
  const navigate = useNavigate();
  const livePrice = useLivePrice(grid.symbol);
  const anomalies = useGridAnomalies(grid.id);

  // Step % computation matching legacy
  const stepPct =
    grid.spacing === "geometric" || grid.spacing === "dynamic"
      ? (Math.pow(grid.upper_price / grid.lower_price, 1 / grid.grid_count) - 1) * 100
      : ((grid.upper_price - grid.lower_price) / grid.grid_count /
          ((grid.lower_price + grid.upper_price) / 2)) *
        100;
  const totalPct =
    grid.spacing === "geometric" || grid.spacing === "dynamic"
      ? (grid.upper_price / grid.lower_price - 1) * 100
      : stepPct * grid.grid_count;

  // Net position from this grid's unpaired legs
  const netPos = unpaired.reduce(
    (s, t) => s + (t.side === "buy" ? 1 : -1) * t.quantity,
    0
  );
  const posLabel = netPos > 0 ? "LONG" : netPos < 0 ? "SHORT" : null;
  const posColor = netPos > 0 ? "success" : "danger";

  // Live unrealized PnL
  let gridUnrealized = 0;
  if (livePrice) {
    for (const t of unpaired) {
      const dir = t.side === "buy" ? 1 : -1;
      const closeFee = livePrice * t.quantity * TAKER_FEE_RATE;
      gridUnrealized += (livePrice - t.price) * t.quantity * dir - (t.fee ?? 0) - closeFee;
    }
  }

  const realized = (pnl?.total_pnl ?? 0) + (pnl?.funding_total ?? 0);
  const gridNet = realized + gridUnrealized;
  const netColor = gridNet >= 0 ? "#22c55e" : "#ef4444";

  const vol = volatility[grid.symbol];
  const levColor = grid.leverage > 1 ? "#a855f7" : "#999";

  const spacingLabel: Record<string, string> = { dynamic: "D", geometric: "G", arithmetic: "A" };
  const spacingTitle: Record<string, string> = {
    dynamic: "Dynamic ATR spacing",
    geometric: "Geometric spacing (uniform %)",
    arithmetic: "Arithmetic spacing (uniform $)",
  };

  return (
    <div
      className="bg-surface border border-border rounded-lg p-3 cursor-pointer hover:border-accent/40 transition-colors"
      onClick={() => navigate(`/grid/${grid.id}`)}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="flex items-baseline gap-1.5 min-w-0">
          <span className="font-semibold text-sm text-text truncate">{grid.symbol}</span>
          <span className="text-[0.72rem] shrink-0">
            <span
              className="font-semibold"
              style={{ color: netColor }}
              data-grid-net={grid.id}
            >
              {gridNet >= 0 ? "+" : ""}
              {fmt(gridNet, 4)}
            </span>
            <span className="text-muted hidden sm:inline">
              {" · "}
              {pnl?.trade_count ?? 0} trade{pnl?.trade_count === 1 ? "" : "s"}
            </span>
          </span>
        </span>

        {/* Badges: wrap to second line on mobile */}
        <div className="flex flex-wrap justify-end gap-1 shrink-0 max-w-[50%] sm:max-w-none">
          <AnomalyBadge anomalies={anomalies} />
          {posLabel && <Badge color={posColor as "success" | "danger"}>{posLabel}</Badge>}
          {spacingLabel[grid.spacing] && (
            <Badge color="warn" title={spacingTitle[grid.spacing]}>
              {spacingLabel[grid.spacing]}
            </Badge>
          )}
          {grid.max_hold_minutes != null && (
            <Badge color="accent" title="Break-even limit exit">
              {fmtDur(grid.max_hold_minutes)}
            </Badge>
          )}
          {grid.force_exit_minutes != null && (
            <Badge color="danger" title="Force market exit">
              {fmtDur(grid.force_exit_minutes)}
            </Badge>
          )}
          {grid.trailing ? <Badge color="info" title="Trailing re-center">TR</Badge> : null}
          {grid.ai_shift_lock_until && new Date(grid.ai_shift_lock_until).getTime() > Date.now() && (
            <Badge color="warn" title={`AI shift lock until ${grid.ai_shift_lock_until} — trailing re-center deferred`}>
              LOCK
            </Badge>
          )}
          {grid.ai_suppress_side === "buy" && (
            <Badge color="warn" title="AI suppressing BUY openers — counter-trend opens unfavorable">
              NO BUY
            </Badge>
          )}
          {grid.ai_suppress_side === "sell" && (
            <Badge color="warn" title="AI suppressing SELL openers — counter-trend opens unfavorable">
              NO SELL
            </Badge>
          )}
          {vol?.mode === "fast" && (
            <Badge color="danger" title={`Fast market — refills paused, BE limits on all openings`}>
              FAST
            </Badge>
          )}
          {vol?.trend === "up" && (
            <Badge color="success" title="Uptrend detected — new shorts suppressed">
              ▲
            </Badge>
          )}
          {vol?.trend === "down" && (
            <Badge color="danger" title="Downtrend detected — new longs suppressed">
              ▼
            </Badge>
          )}
          {grid.status !== "active" && (
            <span className="text-[0.7rem] px-1.5 py-0.5 rounded bg-muted/20 text-muted capitalize">
              {grid.status}
            </span>
          )}
          {/* AI auto-act indicator — present whenever the grid is opted into
              automatic execution of AI verdicts so user can tell at a glance
              which grids can be touched by AI without their click. */}
          {grid.ai_auto_act === 1 && (
            <Badge color="info" title="AI auto-act enabled (PAUSE/RESUME/CUT executed automatically)">
              AI
            </Badge>
          )}
          {/* AI pause countdown — visible while the AI-imposed pause window
              is still active. Label differentiates cause via ai_advice.stage:
                - "initial"  → AI SKIP (creation gated before first placement)
                - "refill"   → AI SKIP (refill tick rejected new placements)
                - runtime    → AI ⏸    (monitor PAUSE advice)
              All three write paused_until; the stage field in the advice
              snapshot tells us which path set it. Click Resume in grid-actions
              clears immediately; otherwise the tick auto-resumes. */}
          {grid.paused_until && new Date(grid.paused_until).getTime() > Date.now() && (
            <Badge
              color="warn"
              title={`${gridPauseCauseTitle(grid.ai_advice)} until ${grid.paused_until}`}
            >
              {gridPauseCauseLabel(grid.ai_advice)} {fmtAiPauseRemaining(grid.paused_until)}
            </Badge>
          )}
          {/* Most-recent AI intervention — parses cycle.ai_advice (snapshot
              of last advice) and shows the action tag so user sees WHAT
              AI just did, not only that it's enabled. */}
          {gridAiLastAct(grid.ai_advice) && (
            <Badge color="accent" title={gridAiLastActTitle(grid.ai_advice)}>
              {gridAiLastAct(grid.ai_advice)}
            </Badge>
          )}
        </div>
      </div>

      {/* Meta row */}
      <div className="text-[0.72rem] text-muted flex flex-wrap gap-x-1.5 gap-y-0.5">
        <span className="text-success">{fmt(grid.lower_price, grid.symbol)}</span>
        <span>–</span>
        <span className="text-danger">{fmt(grid.upper_price, grid.symbol)}</span>
        <span>·</span>
        {grid.spacing === "dynamic" ? (
          <>
            <span className="text-accent">
              {grid.grid_count}×{stepPct.toFixed(2)}%
            </span>
            <span className="text-muted">(ATR×{grid.atr_multiplier ?? 1.5})</span>
          </>
        ) : (
          <>
            <span className="text-info">
              {grid.grid_count}×{stepPct.toFixed(2)}%
            </span>
            <span className="text-muted">({totalPct.toFixed(2)}%)</span>
          </>
        )}
        <span>·</span>
        <span>
          {grid.quantity_per_grid}×
          <span className="font-semibold" style={{ color: levColor }}>
            {grid.leverage}x
          </span>
        </span>
        {/* Required margin replaces the old live-price readout. Same formula as
            the detail-page SYMBOL card: qty × price × (grid_count+1) / leverage
            — notional of max concurrent exposure, divided by leverage. Falls
            back to lower_price when the live feed hasn't populated yet so the
            card still renders something useful on first paint. */}
        {(() => {
          const pxRef = livePrice ?? grid.lower_price;
          if (!(pxRef > 0) || !(grid.leverage > 0)) return null;
          const marginReq = (grid.quantity_per_grid * pxRef * (grid.grid_count + 1)) / grid.leverage;
          return (
            <>
              <span>·</span>
              <span
                className="text-warn"
                title={`Required margin = qty (${grid.quantity_per_grid}) × price × (grid_count+1=${grid.grid_count + 1}) / leverage (${grid.leverage}x)`}
              >
                {fmt(marginReq, 2)} USDT
              </span>
            </>
          );
        })()}
      </div>
    </div>
  );
}

// --- AI helpers (inline — grid-card is the only consumer) ---------------

/** Compact "Xm" / "Xh Ym" remaining string for AI paused_until countdown. */
function fmtAiPauseRemaining(pausedUntil: string): string {
  const remMs = new Date(pausedUntil).getTime() - Date.now();
  if (remMs <= 0) return "0m";
  const totalMin = Math.round(remMs / 60_000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** Latest AI action tag shown as badge. Two advice shapes share this slot:
 *    - Runtime monitor advice:     { act, conf, regime, trigger_reasons[] }
 *    - Pre-placement advice:       { ok, conf, regime, stage, why, risks[] }
 *  Pre-place stage takes priority when present (more specific context).
 *  Only surfaced when advice is fresh (< 30 min) — otherwise a week-old
 *  HOLD would linger giving false impression of recent activity. */
function gridAiLastAct(adviceJson: string | null | undefined): string | null {
  const parsed = parseAdvice(adviceJson);
  if (!parsed) return null;
  // Pre-placement shape: has `stage` field ("initial" | "refill") + `ok`.
  if (parsed.stage === "initial" || parsed.stage === "refill") {
    if (parsed.ok === false) {
      return parsed.stage === "initial" ? "AI:SKIP-INIT" : "AI:SKIP-REFILL";
    }
    if (parsed.ok === true) return null;   // OK is the null action — no badge noise
  }
  // Runtime shape: has `act` field.
  const act = parsed.act ?? parsed.action;
  if (!act || typeof act !== "string") return null;
  if (act === "HOLD") return null;
  if (act === "CUT_LIMIT") return "AI:CUT";
  if (act === "CUT_MARKET") return "AI:CUT";
  if (act === "PAUSE") return "AI:PAUSE";
  if (act === "RESUME") return "AI:RESUME";
  return `AI:${act}`;
}

/** Tooltip for the last-act badge. Works for both runtime + pre-place
 *  shapes. Includes trigger reason for runtime; stage + risks for pre-place. */
function gridAiLastActTitle(adviceJson: string | null | undefined): string {
  const parsed = parseAdvice(adviceJson);
  if (!parsed) return "";
  const conf = parsed.conf != null ? ` conf=${parsed.conf}` : "";
  const why = parsed.why ? ` · ${parsed.why}` : "";
  // Pre-placement advice: describe skip origin + risks.
  if (parsed.stage === "initial" || parsed.stage === "refill") {
    const stageLabel = parsed.stage === "initial" ? "initial placement" : "refill tick";
    const verb = parsed.ok === false ? "skipped" : "allowed";
    const risks = Array.isArray(parsed.risks) && parsed.risks.length
      ? ` · risks: ${parsed.risks.slice(0, 2).join("; ")}`
      : "";
    return `AI ${verb} ${stageLabel}${conf}${why}${risks}`;
  }
  // Runtime monitor advice.
  const trig = Array.isArray(parsed.trigger_reasons) && parsed.trigger_reasons.length
    ? ` · triggered by: ${parsed.trigger_reasons.join("; ")}`
    : "";
  return `Last AI act: ${parsed.act ?? "?"}${conf}${why}${trig}`;
}

/** Label for paused_until countdown — "AI SKIP Xm" when the pause came
 *  from a pre-placement skip, "AI ⏸ Xm" when from a runtime PAUSE. Falls
 *  back to plain "⏸" when the advice is stale/missing (shouldn't happen
 *  in practice since the same write path stamps both). */
function gridPauseCauseLabel(adviceJson: string | null | undefined): string {
  const parsed = parseAdviceNoFreshness(adviceJson);
  if (parsed?.stage === "initial" || parsed?.stage === "refill") return "AI SKIP";
  return "AI ⏸";
}

function gridPauseCauseTitle(adviceJson: string | null | undefined): string {
  const parsed = parseAdviceNoFreshness(adviceJson);
  if (parsed?.stage === "initial") return "AI skipped initial placement — grid paused";
  if (parsed?.stage === "refill") return "AI skipped refill tick — grid paused, existing orders unchanged";
  return "AI paused grid";
}

/** Like parseAdvice() but WITHOUT the 30-min freshness gate — used for the
 *  paused_until label since the pause stamp itself is the freshness proof
 *  (if paused_until is in the future, the advice that caused it is still
 *  "live" from a user perspective regardless of the `at` timestamp). */
function parseAdviceNoFreshness(adviceJson: string | null | undefined): any | null {
  if (!adviceJson) return null;
  try { return JSON.parse(adviceJson); } catch { return null; }
}

/** Shared advice parser with 30-min freshness gate so stale snapshots don't
 *  render fake "AI:CUT" chips. Returns null on malformed / stale / missing. */
function parseAdvice(adviceJson: string | null | undefined): any | null {
  if (!adviceJson) return null;
  try {
    const p = JSON.parse(adviceJson);
    const ts = p.triggered_at ? new Date(p.triggered_at).getTime()
             : p.at ? new Date(p.at).getTime() : 0;
    if (!ts || Date.now() - ts > 30 * 60_000) return null;
    return p;
  } catch {
    return null;
  }
}
